import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import Modal, { Field, Select, TextInput, PrimaryButton, SecondaryButton, GhostButton } from "@/components/FormKit";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { toast } from "sonner";

export default function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assign, setAssign] = useState({ company_id: "", plan_id: "" });
  const [paymentOpen, setPaymentOpen] = useState(null); // subscription obj
  const [payment, setPayment] = useState({ amount: "", method: "bank_transfer", reference: "", notes: "" });

  const load = async () => {
    const [s, c, p] = await Promise.all([
      api.get("/platform/subscriptions"),
      api.get("/platform/companies"),
      api.get("/platform/plans"),
    ]);
    setSubs(s.data);
    setCompanies(c.data);
    setPlans(p.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submitAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/subscriptions", assign);
      toast.success("Subscription assigned");
      setAssignOpen(false);
      setAssign({ company_id: "", plan_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/platform/subscriptions/${paymentOpen.id}/payments`, {
        ...payment,
        amount: Number(payment.amount),
      });
      toast.success("Payment recorded");
      setPaymentOpen(null);
      setPayment({ amount: "", method: "bank_transfer", reference: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this subscription?")) return;
    try {
      await api.post(`/platform/subscriptions/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="platform-subscriptions-page">
      <PageHeader
        eyebrow="Billing"
        title="Subscriptions"
        description="Manual assignment of plans and payment recording per tenant."
        actions={
          <PrimaryButton onClick={() => setAssignOpen(true)} testId="assign-subscription-button">
            + Assign Plan
          </PrimaryButton>
        }
      />

      <DataTable
        testId="subscriptions-table"
        columns={[
          {
            key: "company",
            label: "Company",
            render: (r) => <div className="font-bold">{r.company?.name || "—"}</div>,
          },
          {
            key: "plan",
            label: "Plan",
            render: (r) =>
              r.plan ? (
                <div>
                  <div className="font-semibold">{r.plan.name}</div>
                  <div className="text-xs text-stone-500 font-mono">{fmtCurrency(r.plan.price)}/{r.plan.billing_cycle}</div>
                </div>
              ) : (
                "—"
              ),
          },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "start_date", label: "Start", render: (r) => fmtDate(r.start_date) },
          { key: "end_date", label: "End", render: (r) => fmtDate(r.end_date) },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex gap-1">
                <GhostButton onClick={() => setPaymentOpen(r)} testId={`record-payment-${r.id}`}>
                  Record Payment
                </GhostButton>
                {r.status === "active" && (
                  <GhostButton onClick={() => cancel(r.id)} testId={`cancel-sub-${r.id}`}>
                    Cancel
                  </GhostButton>
                )}
              </div>
            ),
          },
        ]}
        rows={subs}
        empty="No subscriptions yet."
      />

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title="Assign plan to company">
        <form onSubmit={submitAssign}>
          <Field label="Company">
            <Select testId="form-sub-company" required value={assign.company_id} onChange={(e) => setAssign({ ...assign, company_id: e.target.value })}>
              <option value="">Select company…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Plan">
            <Select testId="form-sub-plan" required value={assign.plan_id} onChange={(e) => setAssign({ ...assign, plan_id: e.target.value })}>
              <option value="">Select plan…</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {fmtCurrency(p.price)}/{p.billing_cycle}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setAssignOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-assign">Assign</PrimaryButton>
          </div>
        </form>
      </Modal>

      <Modal open={!!paymentOpen} onClose={() => setPaymentOpen(null)} title="Record subscription payment">
        <form onSubmit={submitPayment}>
          <Field label="Amount (INR)">
            <TextInput testId="form-pay-amount" type="number" step="0.01" required value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
          </Field>
          <Field label="Method">
            <Select testId="form-pay-method" value={payment.method} onChange={(e) => setPayment({ ...payment, method: e.target.value })}>
              <option value="bank_transfer">Bank transfer</option>
              <option value="wire">Wire</option>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </Select>
          </Field>
          <Field label="Reference">
            <TextInput testId="form-pay-reference" value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} />
          </Field>
          <Field label="Notes">
            <TextInput testId="form-pay-notes" value={payment.notes} onChange={(e) => setPayment({ ...payment, notes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setPaymentOpen(null)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-payment">Record</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
